/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import { FaCheck } from 'react-icons/fa';

import { PrimaryButton, CtaButton } from '../../../../../styles/ui/Button';
import {
   ONBOARDING_STATUS,
   ONBOARDING_STATUS_LABEL,
   ONBOARDING_STATUS_HINT,
   ONBOARDING_PIPELINE,
   nextOnboardingStatus,
} from '../../constants';
import {
   Card,
   CardHead,
   Stepper,
   Step,
   StepBanner,
   DisqualifiedBanner,
} from '../styles';

// ---------------------------------------------------------------------------
// Pipeline de admissão (redesign 2026-07-14): dots de 32px com linhas
// conectoras e caption (mock .stepper), painel da etapa ativa no slot
// `stepPanel` (passos manuais — StepWorkPanel) e banner "Etapa atual" com a
// ação de avanço. Avanço é UM PASSO POR VEZ (confirmado num mini-modal do
// pai); em AGUARDANDO_INICIO a saída é ADMITIR (CTA abre o AdmitModal — o azul
// fica no confirmar, G.1). DESQUALIFICADO troca tudo por banner terminal.
// ---------------------------------------------------------------------------

function stepState(step, status) {
   const stepIndex = ONBOARDING_PIPELINE.indexOf(step);
   const statusIndex = ONBOARDING_PIPELINE.indexOf(status);
   if (status === ONBOARDING_STATUS.ADMITIDO) return 'done';
   if (statusIndex === -1) return 'upcoming';
   if (stepIndex < statusIndex) return 'done';
   if (stepIndex === statusIndex) return 'current';
   return 'upcoming';
}

export default function PipelineStepper({
   onboarding,
   canUpdate,
   canAdmit,
   isBusy,
   onRequestAdvance,
   onRequestAdmit,
   stepPanel = null,
   advanceGate = { blocked: false, reason: null },
}) {
   const status = onboarding?.status;
   const next = nextOnboardingStatus(status);
   const isAwaitingStart = status === ONBOARDING_STATUS.AGUARDANDO_INICIO;
   const isAdmitted = status === ONBOARDING_STATUS.ADMITIDO;

   // Gate de completude da etapa (advanceGates.js) — bloqueante =
   // não-acionável (H.3.1): desabilita o avanço e mostra o porquê no banner.
   const advanceBlocked = advanceGate.blocked;

   if (status === ONBOARDING_STATUS.DESQUALIFICADO) {
      return (
         <Card>
            <CardHead>
               <h2>Pipeline de admissão</h2>
            </CardHead>
            <DisqualifiedBanner>
               <div className="banner-title">Candidato desqualificado</div>
               {onboarding?.disqualified_reason && (
                  <div className="banner-reason">
                     {onboarding.disqualified_reason}
                  </div>
               )}
            </DisqualifiedBanner>
         </Card>
      );
   }

   return (
      <Card>
         <CardHead>
            <h2>Pipeline de admissão</h2>
         </CardHead>

         <Stepper>
            {ONBOARDING_PIPELINE.map((step, index) => {
               const state = stepState(step, status);
               return (
                  <Step key={step} $state={state}>
                     <div className="track">
                        <div className="dot">
                           {state === 'done' ? <FaCheck size={13} /> : index + 1}
                        </div>
                     </div>
                     <div className="caption">
                        {ONBOARDING_STATUS_LABEL[step]}
                     </div>
                  </Step>
               );
            })}
            <Step key="ADMITIDO" $state={isAdmitted ? 'done' : 'upcoming'}>
               <div className="track">
                  <div className="dot">
                     {isAdmitted ? <FaCheck size={13} /> : '★'}
                  </div>
               </div>
               <div className="caption">{ONBOARDING_STATUS_LABEL.ADMITIDO}</div>
            </Step>
         </Stepper>

         {/* Painel de trabalho da etapa ativa (passos manuais), quando houver */}
         {!isAdmitted && stepPanel}

         <StepBanner>
            <div className="msg">
               {isAdmitted ? (
                  <>
                     Onboarding concluído — o candidato foi{' '}
                     <b>admitido</b>.
                  </>
               ) : (
                  <>
                     Etapa atual · <b>{ONBOARDING_STATUS_LABEL[status]}</b>
                     <span className="hint">
                        {ONBOARDING_STATUS_HINT[status] ?? ''}
                     </span>
                     {advanceBlocked && advanceGate.reason && (
                        <span
                           className="hint"
                           style={{ color: '#854f0b', fontWeight: 600 }}
                        >
                           {advanceGate.reason}
                        </span>
                     )}
                  </>
               )}
            </div>

            {canUpdate && next && (
               <span
                  title={advanceBlocked ? advanceGate.reason ?? undefined : undefined}
                  style={{ display: 'inline-block' }}
               >
                  <PrimaryButton
                     type="button"
                     disabled={isBusy || advanceBlocked}
                     onClick={() => onRequestAdvance(next)}
                     style={advanceBlocked ? { pointerEvents: 'none' } : undefined}
                  >
                     Avançar para {ONBOARDING_STATUS_LABEL[next]}
                  </PrimaryButton>
               </span>
            )}
            {canAdmit && isAwaitingStart && (
               <CtaButton
                  type="button"
                  disabled={isBusy}
                  onClick={onRequestAdmit}
               >
                  Admitir funcionário…
               </CtaButton>
            )}
         </StepBanner>
      </Card>
   );
}

PipelineStepper.propTypes = {
   onboarding: PropTypes.shape({
      status: PropTypes.string,
      disqualified_reason: PropTypes.string,
   }),
   canUpdate: PropTypes.bool,
   canAdmit: PropTypes.bool,
   isBusy: PropTypes.bool,
   onRequestAdvance: PropTypes.func.isRequired,
   onRequestAdmit: PropTypes.func.isRequired,
   stepPanel: PropTypes.node,
   advanceGate: PropTypes.shape({
      blocked: PropTypes.bool,
      reason: PropTypes.string,
   }),
};
